import { useEffect, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { toast } from 'sonner'
import { api } from '@/store/api'
import { useAppDispatch } from '@/store/hooks'
import { useGetPaymentStatusQuery } from './paymentsApi'
import { useRefreshOnFocus } from '@/lib/useRefreshOnFocus'
import {
  PAYMENT_WAIT_LIMIT_MS,
  phaseOfStatus,
  POLL_MS,
  resolvePhase,
  type PaymentPhase,
} from './lib/paymentPhase'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface PaymentWatch {
  phase: PaymentPhase
  /** Серверное пояснение к неуспеху, если оно пришло. */
  message: string | null
  /** Ручное перечитывание баланса — единственный выход в деградации. */
  refresh: () => void
}

/**
 * Ожидание оплаты, открытой в соседней вкладке. Живёт в провайдере, а не в
 * диалоге: закрытый диалог не должен убивать ожидание — успех тогда прилетает
 * тостом.
 *
 * Остановка опроса — `pollingInterval: 0`, а НЕ `skip`: `skip` снимает
 * подписку и уносит с собой терминальный ответ (тот же урок, что у
 * `JobPoller`). `skipToken` ниже — другое: транзакции нет вовсе, опрашивать
 * нечего.
 */
export const usePaymentWatcher = (
  transactionId: number | null,
  isDialogOpen: boolean,
): PaymentWatch => {
  const dispatch = useAppDispatch()
  // Опции хука не могут читать его собственную `data` (переменная ещё не
  // объявлена) — терминальность, деградация и истёкшее ожидание держатся
  // отдельным состоянием. Хранится не булев флаг, а ИД транзакции, для которой
  // признак получен: сбрасывать его на смене транзакции нечем и незачем — он
  // просто перестаёт совпадать. Эффект-сброс на `[transactionId]` показывал бы
  // новой покупке фазу прошлой ещё один рендер (эффекты выполняются ПОСЛЕ
  // рендера), а деградация у нас не редкий исход.
  const [stoppedFor, setStoppedFor] = useState<number | null>(null)
  const [degradedFor, setDegradedFor] = useState<number | null>(null)
  const [expiredFor, setExpiredFor] = useState<number | null>(null)
  const isDegraded = transactionId !== null && degradedFor === transactionId
  const isExpired = transactionId !== null && expiredFor === transactionId
  const isStopped = transactionId !== null && stoppedFor === transactionId
  // `currentData` (не `data`): `data` хук отдаёт «последний успешный ответ
  // ЛЮБОГО аргумента» и на смене транзакции ещё держит ответ прошлой — новая
  // покупка секунду выглядела бы уже оплаченной.
  const { currentData, error, refetch, startedTimeStamp } = useGetPaymentStatusQuery(
    transactionId ?? skipToken,
    { pollingInterval: isStopped || isDegraded || isExpired ? 0 : POLL_MS },
  )

  useEffect(() => {
    const status = (error as { status?: number } | undefined)?.status
    // 404/403: транзакции нет или она чужая (§1 ответа бэка — «опрос
    // прекратить»); кредитные транзакции ручка ПОКРЫВАЕТ, так что это не
    // пробел контракта, а конец наблюдения. Опрос гасим навсегда — повтор
    // упрётся в тот же ответ, а вечный спиннер соврал бы, что мы ждём
    // достижимого. Экран при этом не тупиковый: остаётся «Обновить баланс».
    if (transactionId !== null && (status === 404 || status === 403)) setDegradedFor(transactionId)
  }, [error, transactionId])

  // Отсчёт ведётся от начала наблюдения за ЭТОЙ транзакцией; проверка висит на
  // `startedTimeStamp` — он меняется на каждой попытке опроса, то есть предел
  // срабатывает ровно тогда, когда очередной ответ (или очередной 5xx) опять
  // не принёс исхода.
  const watchStartedAt = useRef(0)
  useEffect(() => {
    watchStartedAt.current = Date.now()
  }, [transactionId])
  useEffect(() => {
    if (transactionId === null || isStopped) return
    if (Date.now() - watchStartedAt.current <= PAYMENT_WAIT_LIMIT_MS) return
    setExpiredFor(transactionId)
  }, [transactionId, startedTimeStamp, isStopped])

  const paidFor = useRef<number | null>(null)
  useEffect(() => {
    if (transactionId === null || !currentData) return
    // Фаза, а не сырой статус: `completed` приходит строчным (контракт), а
    // плеер шлёт `COMPLETED` — сравнение с литералом промахнулось бы мимо
    // одного из них, и оплата не зачислялась бы молча.
    const phase = phaseOfStatus(currentData.status)
    if (phase === 'waiting') return
    setStoppedFor(transactionId)
    if (phase !== 'completed' || paidFor.current === transactionId) return
    paidFor.current = transactionId
    // Баланс только перечитыванием (§1.4), и смета каждого курса несёт его
    // внутри себя — поэтому `Quote` инвалидируется целиком, без id.
    dispatch(api.util.invalidateTags(['CreditsBalance', 'Quote', 'CreditsHistory']))
    if (!isDialogOpen) toast.success(messagesNow(messages).balanceToppedUp)
  }, [currentData, dispatch, isDialogOpen, transactionId])

  const refresh = () => {
    dispatch(api.util.invalidateTags(['CreditsBalance', 'Quote', 'CreditsHistory']))
    // Без транзакции запрос идёт `skipToken` и вовсе не стартовал — `refetch()`
    // синхронно бросил бы «Cannot refetch a query that has not been started
    // yet». Перечитать баланс без транзакции — осмысленно, оно выше и остаётся.
    if (transactionId === null || isDegraded) return
    void refetch()
  }
  // Пока платёж ждут, возврат на вкладку студии — самый честный повод
  // перечитать баланс (§7).
  useRefreshOnFocus(transactionId !== null, refresh)

  if (transactionId === null) return { phase: 'idle', message: null, refresh }
  return {
    phase: resolvePhase(
      currentData ? phaseOfStatus(currentData.status) : null,
      isDegraded,
      isExpired,
    ),
    message: currentData?.message ?? null,
    refresh,
  }
}
